import React, { useEffect, useState } from "react";
import { getDashboardStats } from "../services/database";
import { logout } from "../services/auth";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";
import Spinner from "./Spinner";
import Sidebar from "./Sidebar";
import DashboardContent from "./DashboardContent";
import UserList from "./UserList";
import BlogPage from "./BlogPage";
import DiscountCampaignManager from "./DiscountCampaignManager";

// Registering Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  last_login?: string;
  next_debit?: string;
  total_spent?: number;
  total_refunds?: number;
  avatar?: string;
  subscription: {
    plan: "free" | "premium" | "pro";
    status: "active" | "canceled" | "expired" | "past_due" | "unpaid";
    billing: "monthly" | "yearly";
    expiresAt: string;
    renewsAt?: string;
    cancelAt?: string;
    features: string[];
    stripeSubscriptionId?: string;
    stripeCustomerId?: string;
    priceId?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
  } | null;
}

interface MonthlyData {
  [month: string]: {
    recipes: number;
    meal_plans: number;
  };
}

interface DashboardStats {
  new_users_this_month: number;
  users_by_type: {
    free: number;
    pro: number;
    premium: number;
  };
  all_users: User[];
  recipes_current_month: number;
  meal_plans_current_month: number;
  total_recipes: number;
  total_meal_plans: number;
  monthly_data: MonthlyData;
  current_month: number;
  current_year: number;
}

interface PeriodFilter {
  startDate: string | null;
  endDate: string | null;
}

interface PeriodStats {
  period: {
    startDate: string | null;
    endDate: string | null;
  };
  users: {
    free: number;
    pro: number;
    premium: number;
    total: number;
  };
  content: {
    recipes: number;
    meal_plans: number;
  };
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string>("dashboard");
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>({
    startDate: null,
    endDate: null,
  });
  const [periodStats, setPeriodStats] = useState<PeriodStats | null>(null);
  const [loadingPeriod, setLoadingPeriod] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await getDashboardStats();
        setStats(data);
        setError(null);
      } catch (err) {
        console.error("Error loading dashboard statistics:", err);
        setError("Could not load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchPeriodStats = async () => {
      if (periodFilter.startDate || periodFilter.endDate) {
        try {
          setLoadingPeriod(true);
          // Ensuring the /api prefix is included in the URL
          const baseUrl =
            process.env.REACT_APP_API_URL || "http://localhost:8000";

          const params = new URLSearchParams();

          if (periodFilter.startDate) {
            params.append("startDate", periodFilter.startDate);
          }

          if (periodFilter.endDate) {
            params.append("endDate", periodFilter.endDate);
          }

          const response = await axios.get(
            `${baseUrl}/api/admin/dashboard/stats/period?${params.toString()}`
          );
          setPeriodStats(response.data);
        } catch (err) {
          console.error("Error loading period statistics:", err);
          // Reset period stats on error
          setPeriodStats(null);
          if (axios.isAxiosError(err) && err.response?.status === 400) {
            console.error("Validation error:", err.response.data?.error || "Invalid date range");
          }
        } finally {
          setLoadingPeriod(false);
        }
      } else {
        // Clear period stats if no date filter is applied
        setPeriodStats(null);
        setLoadingPeriod(false);
      }
    };

    fetchPeriodStats();
  }, [periodFilter]);

  // Function to refresh stats when user is updated
  const refreshStats = async () => {
    try {
      const data = await getDashboardStats();
      setStats(data);
    } catch (err) {
      console.error("Error refreshing dashboard statistics:", err);
    }
  };

  // Function to handle user updates from UserList
  const handleUserUpdate = async (updatedUser: any) => {
    if (updatedUser.action === 'delete') {
      // Remove user from local state
      setStats(prevStats => {
        if (!prevStats) return prevStats;
        return {
          ...prevStats,
          all_users: prevStats.all_users.filter(user => user.id !== updatedUser.userId)
        };
      });
    } else {
      // Update user in local state
      setStats(prevStats => {
        if (!prevStats) return prevStats;
        return {
          ...prevStats,
          all_users: prevStats.all_users.map(user => 
            user.id === updatedUser.id ? { ...user, ...updatedUser } : user
          )
        };
      });
    }
    
    // Refresh full stats to ensure everything is up to date
    await refreshStats();
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleViewChange = (view: string) => {
    setActiveView(view);
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const startDate = e.target.value || null;
    setPeriodFilter((prev) => ({ ...prev, startDate }));
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const endDate = e.target.value || null;
    setPeriodFilter((prev) => ({ ...prev, endDate }));
  };

  const handleClearFilter = () => {
    setPeriodFilter({ startDate: null, endDate: null });
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-50">
        <Spinner size="lg" />
        <p className="mt-4 text-gray-600 font-medium">
          Loading dashboard statistics...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 m-4 bg-red-50 border border-red-200 text-red-700 rounded-lg shadow flex items-center">
        <div className="mr-3 flex-shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div>
          <p className="font-medium">{error}</p>
          <p className="text-sm">
            Please try again or contact support.
          </p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const renderCurrentView = () => {
    switch (activeView) {
      case "dashboard":
        return (
          <DashboardContent
            stats={stats}
            periodFilter={periodFilter}
            periodStats={periodStats}
            loadingPeriod={loadingPeriod}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            onClearFilter={handleClearFilter}
          />
        );
      case "users":
        return <UserList users={stats.all_users} onUserUpdate={handleUserUpdate} />;
      case "blog":
        return <BlogPage />;
      case "campaigns":
        return <DiscountCampaignManager />;
      case "analytics":
        return (
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Analytics</h2>
            <p className="text-gray-600">Advanced analytics coming soon...</p>
          </div>
        );
      case "subscriptions":
        return (
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Subscriptions</h2>
            <p className="text-gray-600">Subscription management coming soon...</p>
          </div>
        );
      case "performance":
        return (
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Performance</h2>
            <p className="text-gray-600">Performance metrics coming soon...</p>
          </div>
        );
      case "settings":
        return (
          <div className="p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Settings</h2>
            <p className="text-gray-600">Admin settings coming soon...</p>
          </div>
        );
      default:
        return (
          <DashboardContent
            stats={stats}
            periodFilter={periodFilter}
            periodStats={periodStats}
            loadingPeriod={loadingPeriod}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
            onClearFilter={handleClearFilter}
          />
        );
    }
  };

  return (
    <div className="h-screen flex bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0">
        <Sidebar
          activeView={activeView}
          onViewChange={handleViewChange}
          onLogout={handleLogout}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className="flex-1 overflow-y-auto p-6">
          {renderCurrentView()}
        </main>
      </div>
    </div>
  );
};
export default Dashboard;
